import { ReactElement, useMemo } from 'react'
import {
  GetStaticPathsContext,
  GetStaticPropsContext,
  InferGetStaticPropsType
} from 'next'

import Meta from '@/components/base/Meta'
import Blog from '@/components/layout/Blog'
import Builder from '@/components/layout/Builder'
import Collection from '@/components/layout/Collection'
import Contact from '@/components/layout/Contact'
import Layout from '@/components/layout/Layout'
import MoodArchive from '@/components/layout/MoodArchive'
import Policy from '@/components/layout/Policy'
import Product from '@/components/layout/Product'
import Project from '@/components/layout/Project'
import Hero from '@/components/sections/Hero'
import usePagePrecache from '@/hooks/usePagePrecache'
import { mapArchiveStaticProps, mapPageStaticProps } from '@/utils/pageData'
import { getPromiseFromFetch } from '@/utils/request'
import { API } from '@/constants'

import { WPPageData, WPRoutes } from '@/types'

export default function Page(
  pageData: InferGetStaticPropsType<typeof getStaticProps>
) {
  usePagePrecache(true, [])

  const output = useMemo(() => {
    if (pageData.template === 'template/archive-news.php') {
      return <Blog pageData={pageData} />
    }
    if (pageData.template === 'template/archive-projects.php') {
      return <Builder pageData={mapArchiveStaticProps(pageData)} />
    }
    if (pageData.template === 'template/archive-mood.php') {
      return <MoodArchive pageData={pageData} />
    }
    if (pageData.template === 'template/contact.php') {
      return <Contact pageData={pageData} />
    }
    if (pageData.template === 'template/collection.php') {
      return <Collection pageData={pageData} />
    }
    if (pageData.template === 'template/policy.php') {
      return <Policy pageData={pageData} />
    }
    if (
      pageData.type === 'progetto' ||
      pageData.type === 'post' ||
      pageData.type === 'mood'
    ) {
      return <Project pageData={pageData} is_post={pageData.type === 'post'} />
    }
    if (pageData.type === 'collezione') {
      return <Product pageData={pageData} />
    }
    return (
      <>
        <Hero {...pageData.acf.hero} />
        <Builder pageData={pageData} />
      </>
    )
  }, [pageData])

  return output
}

Page.getLayout = function getLayout(page: ReactElement, props: WPPageData) {
  return (
    <Layout pageData={props}>
      <Meta pageData={props} />
      {page}
    </Layout>
  )
}

export async function getStaticProps({
  params,
  locale
}: GetStaticPropsContext<{ routes: string[] }>) {
  const res = await getPromiseFromFetch<WPPageData>({
    method: 'get',
    url: `/${locale}/${params?.routes.join('/')}/`
  })

  if (locale === 'default' || res.status !== 200) return { notFound: true }

  const { data: pageData } = res

  return {
    props: {
      ...(await mapPageStaticProps(pageData))
    }
  }
}

export async function getStaticPaths({}: GetStaticPathsContext) {
  const { data: routes } = await getPromiseFromFetch<WPRoutes>({
    method: 'get',
    url: API.ROUTES
  })

  return {
    paths: routes.map((route) => ({
      params: { routes: route.routes.filter((item) => item !== route.locale) },
      locale: route.locale
    })),
    fallback: 'blocking' // false
  }
}
